import { LocalCache } from '../../jsx/cache/cache';
import { AuthBase } from '../../plunk/service/auth-base';
import Service from '../../plunk/service/service';
import { signIn as cognitoSignIn, signOut as cognitoSignOut } from '../auth/cognito';
import { ssoEligible, isSsoMode, setSsoMode, setSsoTenant, setSsoTenantConfig, fetchSsoSession, brokerLogin, brokerLogout, redirectToCentralLogin } from '../auth/sso';
import Cache from '../store/cache';
import account from '../data/account';

// Front-door auth API used by views and base components.
//
// On *.plunk.io: sign-in goes THROUGH the auth broker (auth.plunk.io) so a
// single sign-in covers all Plunk apps (httpOnly .plunk.io cookie); tokens
// come back from the broker and refresh via the broker (no client-side
// refresh token). On localhost dev: direct Cognito, unchanged.
//
// Tokens persist in localStorage('plunkOAuthTokens') via AuthBase either
// way; refresh + Bearer injection happen inside the Service framework.

// Per-app access gating (custom:apps — comma list of "studio"/"app").
// ABSENT/empty → both allowed (siloing is opt-in per user); administrator/root
// always get both. Mirrors the broker's AllowedAppsFrom + Studio's allowedApps.
function appAccessAllowed(session) {
  const claims = session && session.claims || {};
  if (Array.isArray(claims.apps) && claims.apps.length) return claims.apps.includes('app');
  const groups = (claims.groups || []).map(x => String(x).toLowerCase());
  if (groups.includes('administrator') || groups.includes('root')) return true;
  const list = String(claims['custom:apps'] || '').split(',').map(x => x.trim().toLowerCase()).map(x => x === 'apps' ? 'app' : x).filter(x => x === 'studio' || x === 'app');
  return list.length === 0 || list.includes('app');
}
function storeSsoTokens(session, email) {
  Service.tokens = {
    idToken: session.idToken,
    accessToken: session.accessToken,
    refreshToken: null,
    // lives server-side in the broker
    expiresAt: session.expiresAt
  };
  setSsoMode(true);
  // Acting tenant from the broker (privileged users' picked workspace;
  // others' account claim). account.get() prefers this over the raw claim.
  setSsoTenant(session.tenant || null);
  // Per-tenant config from the registry (domain, label, …). NEVER
  // downgrade to null on a config-less session response — a transient
  // registry/broker hiccup would wipe features + modules and every
  // gated dashboard section (Funnel / Sales / tenant modules) vanishes
  // until the next successful refresh (seen on Fairfield 2026-07-31).
  if (session.tenantConfig) setSsoTenantConfig(session.tenantConfig);
  const claimEmail = session.claims && session.claims.email || email;
  if (claimEmail) LocalCache.set('authTokens', {
    email: claimEmail
  });
}
export default class {
  static isAuthenticated() {
    return AuthBase.hasIdToken;
  }

  // Refresh tokens via the broker (SSO mode keeps the refresh token
  // server-side — client-side cognitoRefresh has nothing to work with).
  // Returns the fresh idToken, or null when not in SSO mode / no session.
  static async refreshSsoTokens() {
    if (!isSsoMode()) return null;
    const session = await fetchSsoSession();
    if (!session) return null;
    storeSsoTokens(session);
    return session.idToken || null;
  }

  // Silent SSO attach: adopt an existing broker session (user signed in
  // via another Plunk app). Returns true when a session was adopted.
  // Registry edits (module manifests, feed URLs, feature flags) reach
  // ALREADY-SIGNED-IN sessions here: a once-per-boot broker session
  // re-read that refreshes the stored tenantConfig. Without it a session
  // kept the config from sign-in time forever (Studio had the identical
  // bug — fixed there 2026-07-20). No-op on localhost / when signed out.
  static async refreshTenantConfig() {
    // Deliberately NOT gated on isSsoMode(): the config re-read only
    // needs the broker cookie, and sessions stored before the SSO-mode
    // marker existed never carried the flag — they were frozen at
    // sign-in-time config forever (Ambella missing-Configurations-menu
    // bug, 2026-08-11). Token adoption still respects the mode elsewhere.
    if (!ssoEligible() || !AuthBase.hasIdToken) return false;
    try {
      const session = await fetchSsoSession();
      if (!session) return false;
      setSsoTenant(session.tenant || null);
      // Keep the stored config when the fresh session lacks one —
      // same no-downgrade rule as storeSsoTokens above.
      if (session.tenantConfig) setSsoTenantConfig(session.tenantConfig);
      return true;
    } catch (e) {
      return false;
    }
  }
  static async trySsoAttach() {
    if (!ssoEligible() || AuthBase.hasIdToken) return false;
    const session = await fetchSsoSession();
    if (!session) return false;
    // Siloed to Studio (custom:apps without "app") → this user never
    // enters the App; hand them to Studio instead of an error trap.
    if (!appAccessAllowed(session)) {
      location.replace('https://studio.plunk.io');
      return false;
    }
    storeSsoTokens(session);
    return true;
  }

  // View auth guards call this when NOT locally authenticated. Tries a
  // silent SSO attach IN PLACE first (so an SSO landing never navigates to
  // /Auth/SignIn and back — that double-hop is the sign-in flicker). Returns
  // true when a session was adopted (caller proceeds to render/fetch);
  // otherwise navigates to the sign-in route and returns false.
  // Shopify landings (?shopify=…): the broker handoff may have signed in a DIFFERENT login than the
  // one this browser last used in the App (e.g. a second dev store's alias login while the Studio
  // dev login was still cached locally, 2026-09-26). Studio already re-adopts the broker identity
  // in that case; the App must too, or the merchant sees the wrong workspace. Returns true when
  // the local session was replaced — the caller reloads so every view re-reads the account.
  static async adoptBrokerIdentityIfDifferent() {
    try {
      if (!ssoEligible()) return false;
      const q = new URLSearchParams(location.search || '');
      if (!q.get('shopify')) return false;
      const session = await fetchSsoSession();
      if (!session || !session.idToken) return false;
      const claims = session.claims || {};
      const brokerTenant = String(session.tenant || claims.account || claims['custom:account'] || '').toLowerCase();
      const brokerEmail = String(claims.email || '').toLowerCase();
      const local = account.get() || {};
      const localTenant = String(local.tenantId || '').toLowerCase();
      const localEmail = String((LocalCache.get('authTokens') || {}).email || '').toLowerCase();
      const same = localTenant && brokerTenant === localTenant && (!brokerEmail || !localEmail || brokerEmail === localEmail);
      if (same) return false;
      if (!appAccessAllowed(session)) return false;
      storeSsoTokens(session);
      try {
        Cache.clear();
      } catch (e) {/* no cache to clear */}
      return true;
    } catch (e) {
      return false;
    }
  }
  static async attachOrSignIn() {
    try {
      if (await this.trySsoAttach()) return true;
    } catch (e) {/* fall through */}
    this.goSignIn();
    return false;
  }

  // Navigate to sign-in. On *.plunk.io this is the CENTRAL login
  // (auth.plunk.io), with a ?redirect back to the exact page requested so
  // deep links survive. Localhost dev uses the app's own /Auth/SignIn form
  // (no broker), remembering the deep link in sessionStorage.
  static goSignIn() {
    if (ssoEligible()) {
      redirectToCentralLogin(location.origin + location.pathname + location.search);
      return;
    }
    try {
      if (!/^\/Auth\//i.test(location.pathname)) {
        sessionStorage.setItem('plunkReturnUrl', location.pathname + location.search);
      }
    } catch (e) {/* ignore */}
    location.href = '/Auth/SignIn';
  }

  // Where to land after sign-in: the remembered deep link, else home.
  static returnUrl() {
    try {
      const v = sessionStorage.getItem('plunkReturnUrl');
      sessionStorage.removeItem('plunkReturnUrl');
      return v && !/^\/Auth\//i.test(v) ? v : '/';
    } catch (e) {
      return '/';
    }
  }
  static async signIn(email, password) {
    const normalized = (email || '').toLowerCase().trim();
    if (ssoEligible()) {
      // Broker sign-in sets the SSO cookie for every *.plunk.io app.
      await brokerLogin(normalized, password);
      const session = await fetchSsoSession();
      if (!session) {
        const err = new Error('Sign-in succeeded but no session was returned.');
        err.code = 'NetworkError';
        throw err;
      }
      if (!appAccessAllowed(session)) {
        location.replace('https://studio.plunk.io');
        return false;
      }
      storeSsoTokens(session, normalized);
      return true;
    }

    // Localhost dev: direct Cognito (no .plunk.io cookie possible).
    const result = await cognitoSignIn(normalized, password);
    Service.tokens = {
      idToken: result.idToken,
      accessToken: result.accessToken,
      refreshToken: result.refreshToken,
      expiresAt: result.expiresIn ? result.expiresIn * 1000 : null
    };
    setSsoMode(false);
    setSsoTenant(null);
    setSsoTenantConfig(null);
    // Mirror email into the legacy cache so account.getEmail() works
    // even before a render reads it from the JWT.
    LocalCache.set('authTokens', {
      email: normalized
    });
    return true;
  }
  static async SignOut() {
    // SSO sessions must also kill the broker session — otherwise the
    // silent attach signs the user straight back in on the next load
    // (and other apps would stay signed in). Await it so the .plunk.io
    // cookie is cleared before we hand off to auth.plunk.io.
    const wasSso = isSsoMode();
    setSsoMode(false);
    setSsoTenant(null);
    setSsoTenantConfig(null);
    cognitoSignOut();
    Cache.remove();
    Service.removeAllTokens();
    LocalCache.delete('authTokens');
    LocalCache.delete('plunk-tenant');
    if (wasSso) {
      try {
        await brokerLogout();
      } catch (e) {/* ignore */}
    }
    // On *.plunk.io, sign-out returns to the central login WITH a ?redirect
    // back to this app, so signing in again lands here (the last app used).
    // Localhost dev keeps the in-app sign-out screen.
    if (ssoEligible()) {
      redirectToCentralLogin(window.location.origin);
    }
  }
  static get() {
    return AuthBase.tokens;
  }
  static store(tokens) {
    Service.tokens = tokens;
  }
}